import { useEffect, useMemo, useRef, useState } from "react";

export type NotificationLevel = "info" | "warning" | "danger" | "success";

export type NotificationItem = {
  id: string;
  title: string;
  description: string;
  level: NotificationLevel;
  href?: string;
};

type NotificationBellProps = {
  notifications: NotificationItem[];
  storageKey: string;
  emptyText?: string;
  onNavigate?: (href: string) => void;
};

type DismissEntry = { sig: string; at: number };

const REDISMISS_AFTER_MS = 24 * 60 * 60 * 1000;
const MAX_DISMISS_ENTRIES = 200;

function parseReadEntries(raw: string | null): DismissEntry[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const fallbackAt = Date.now();
    return parsed
      .map((item): DismissEntry | null => {
        if (typeof item === "string" && item.trim() !== "") {
          return { sig: item, at: fallbackAt };
        }
        if (
          item &&
          typeof item === "object" &&
          typeof (item as { sig?: unknown }).sig === "string" &&
          typeof (item as { at?: unknown }).at === "number"
        ) {
          return { sig: (item as DismissEntry).sig, at: (item as DismissEntry).at };
        }
        return null;
      })
      .filter((entry): entry is DismissEntry => entry !== null);
  } catch {
    return [];
  }
}

function buildNotificationSignature(notification: NotificationItem): string {
  return JSON.stringify([
    notification.id,
    notification.level,
    notification.title,
    notification.description,
    notification.href ?? "",
  ]);
}

function getLevelStyles(level: NotificationLevel): { border: string; dot: string; badgeBg: string; badgeText: string } {
  if (level === "danger") {
    return {
      border: "rgba(248,113,113,0.35)",
      dot: "#f87171",
      badgeBg: "rgba(248,113,113,0.15)",
      badgeText: "#fca5a5",
    };
  }
  if (level === "warning") {
    return {
      border: "rgba(251,191,36,0.35)",
      dot: "#fbbf24",
      badgeBg: "rgba(251,191,36,0.15)",
      badgeText: "#fcd34d",
    };
  }
  if (level === "success") {
    return {
      border: "rgba(74,222,128,0.35)",
      dot: "#4ade80",
      badgeBg: "rgba(74,222,128,0.15)",
      badgeText: "#86efac",
    };
  }
  return {
    border: "rgba(34,211,238,0.35)",
    dot: "#22d3ee",
    badgeBg: "rgba(34,211,238,0.15)",
    badgeText: "#67e8f9",
  };
}

export default function NotificationBell({
  notifications,
  storageKey,
  emptyText = "Aktuell keine Benachrichtigungen.",
  onNavigate,
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [readEntries, setReadEntries] = useState<DismissEntry[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const notificationsWithSignatures = useMemo(
    () => notifications.map((notification) => ({ notification, signature: buildNotificationSignature(notification) })),
    [notifications],
  );

  useEffect(() => {
    setReadEntries(parseReadEntries(localStorage.getItem(storageKey)));
    const sync = (event: StorageEvent) => {
      if (event.key === storageKey) setReadEntries(parseReadEntries(event.newValue));
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [storageKey]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const visibleNotifications = useMemo(() => {
    const dismissedAt = new Map<string, number>();
    readEntries.forEach((entry) => {
      const existing = dismissedAt.get(entry.sig);
      if (existing === undefined || entry.at > existing) dismissedAt.set(entry.sig, entry.at);
    });
    const now = Date.now();
    return notificationsWithSignatures.filter(({ notification, signature }) => {
      const at = dismissedAt.get(notification.id) ?? dismissedAt.get(signature);
      if (at === undefined) return true;
      if (notification.level === "danger" && now - at >= REDISMISS_AFTER_MS) return true;
      return false;
    });
  }, [notificationsWithSignatures, readEntries]);

  const unreadCount = visibleNotifications.length;

  const writeEntries = (entries: DismissEntry[]) => {
    // Nicht gegen die momentan sichtbare Liste bereinigen: Sie ist beim Start
    // zunächst leer, während die API-Daten noch laden. Genau das löschte bisher
    // jeden gespeicherten Gelesen-Status bei jedem Reload. Das feste Limit hält
    // den lokalen Speicher trotzdem klein.
    const bounded = entries.slice(-MAX_DISMISS_ENTRIES);
    setReadEntries(bounded);
    localStorage.setItem(storageKey, JSON.stringify(bounded));
  };

  const dismiss = (notification: NotificationItem, signature: string) => {
    const now = Date.now();
    const without = readEntries.filter((entry) => entry.sig !== notification.id && entry.sig !== signature);
    writeEntries([...without, { sig: notification.id, at: now }, { sig: signature, at: now }]);
  };

  const dismissAll = () => {
    const now = Date.now();
    const sigs = new Set(visibleNotifications.flatMap(({ notification, signature }) => [notification.id, signature]));
    const keep = readEntries.filter((entry) => !sigs.has(entry.sig));
    const added: DismissEntry[] = Array.from(sigs).map((sig) => ({ sig, at: now }));
    writeEntries([...keep, ...added]);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          background: isOpen ? "rgba(249,115,22,0.1)" : "transparent",
          border: isOpen ? "1px solid rgba(249,115,22,0.35)" : "1px solid rgba(71,85,105,0.4)",
          borderRadius: "8px",
          padding: "0.375rem 0.5rem",
          color: unreadCount > 0 ? "#f1f5f9" : "#64748b",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
        title="Benachrichtigungen"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.244.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9zm4.502 8.9a2.25 2.25 0 104.496 0 25.057 25.057 0 01-4.496 0z" clipRule="evenodd" />
        </svg>
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              minWidth: "16px",
              height: "16px",
              borderRadius: "999px",
              background: "#ef4444",
              color: "white",
              fontSize: "0.65rem",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              border: "2px solid #1e293b",
              lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: "360px",
            maxWidth: "calc(100vw - 2rem)",
            background: "#111827",
            border: "1px solid rgba(71,85,105,0.4)",
            borderRadius: "12px",
            boxShadow: "0 18px 40px rgba(15,23,42,0.55)",
            overflow: "hidden",
            zIndex: 30,
          }}
        >
          <div
            style={{
              padding: "0.85rem 1rem",
              borderBottom: "1px solid rgba(71,85,105,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
            }}
          >
            <div>
              <div style={{ fontSize: "0.85rem", fontWeight: "700", color: "#f1f5f9" }}>Benachrichtigungen</div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)" }}>
                {unreadCount > 0 ? `${unreadCount} offen` : "Nichts Neues"}
              </div>
            </div>
            <button
              onClick={dismissAll}
              disabled={unreadCount === 0}
              style={{
                background: "transparent",
                border: "1px solid rgba(71,85,105,0.4)",
                borderRadius: "6px",
                color: unreadCount === 0 ? "var(--text-medium)" : "#94a3b8",
                fontSize: "0.7rem",
                fontWeight: "600",
                padding: "0.28rem 0.5rem",
                cursor: unreadCount === 0 ? "default" : "pointer",
              }}
            >
              Alle ausblenden
            </button>
          </div>

          <div style={{ maxHeight: "360px", overflowY: "auto", padding: "0.6rem" }}>
            {visibleNotifications.length === 0 ? (
              <div
                style={{
                  border: "1px dashed rgba(71,85,105,0.4)",
                  borderRadius: "10px",
                  padding: "1rem",
                  fontSize: "0.8rem",
                  color: "var(--text-secondary)",
                  textAlign: "center",
                }}
              >
                {emptyText}
              </div>
            ) : (
              visibleNotifications.map(({ notification, signature }) => {
                const levelStyles = getLevelStyles(notification.level);

                return (
                  <div
                    key={signature}
                    style={{
                      border: `1px solid ${levelStyles.border}`,
                      borderRadius: "10px",
                      background: "rgba(15,23,42,0.65)",
                      padding: "0.75rem 0.85rem",
                      marginBottom: "0.55rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                          <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: levelStyles.dot, flexShrink: 0 }} />
                          <span style={{ fontSize: "0.78rem", fontWeight: "700", color: "#f1f5f9" }}>{notification.title}</span>
                          <span
                            style={{
                              background: levelStyles.badgeBg,
                              color: levelStyles.badgeText,
                              fontSize: "0.62rem",
                              fontWeight: "700",
                              borderRadius: "999px",
                              padding: "0.1rem 0.42rem",
                            }}
                          >
                            Neu
                          </span>
                        </div>
                        <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", lineHeight: 1.4 }}>{notification.description}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => dismiss(notification, signature)}
                        title="Ausblenden"
                        aria-label="Ausblenden"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#94a3b8",
                          cursor: "pointer",
                          fontSize: "1.05rem",
                          lineHeight: 1,
                          padding: "0 0.1rem",
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    </div>
                    {notification.href && onNavigate && (
                      <button
                        onClick={() => {
                          dismiss(notification, signature);
                          onNavigate(notification.href ?? "/");
                          setIsOpen(false);
                        }}
                        style={{
                          marginTop: "0.6rem",
                          background: "rgba(249,115,22,0.1)",
                          border: "1px solid rgba(249,115,22,0.35)",
                          color: "#fb923c",
                          borderRadius: "6px",
                          fontSize: "0.72rem",
                          fontWeight: "600",
                          padding: "0.28rem 0.55rem",
                          cursor: "pointer",
                        }}
                      >
                        Öffnen
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
